import { DocumentChatCellComponent } from '@shared/components/wcg-table/table-cell/document-chat-cell/document-chat-cell.component';
import { DocumentQcStatusCellComponent } from '@shared/components/wcg-table/table-cell/document-qc-status-cell/document-qc-status-cell.component';
import { DocumentUploadStatusCellComponent } from '@shared/components/wcg-table/table-cell/document-upload-status-cell/document-upload-status-cell.component';
import { LinkActionCellComponent } from '@shared/components/wcg-table/table-cell/link-action-cell/link-action-cell.component';
import {
  Document,
  DocumentAttributeType,
  DocumentListItem,
  DocumentType,
  DocumentTypeFilter,
  InProgressDocument,
  RequiredDocumentType,
} from '@documents/models/document-type';
import {
  HeaderCellConfiguration,
  WcgAdvancedFilterType,
  WcgDateTimePipe,
} from '@wcg/frontend-framework';

export function getFileUploadCell(
  cellData: string,
  item: any,
  canEdit: boolean,
  stage: string,
  uploadRequiredDoc: (data: {
    document: DocumentListItem;
    isUploaded: boolean;
  }) => void,
  onCancelUpload: (documentId: string) => void,
): any {
  return {
    [cellData]: {
      componentType: item?.newUpload
        ? DocumentUploadStatusCellComponent
        : LinkActionCellComponent,
      cellData: item.title || '',
      componentConfig: {
        rowData: item,
        readonly: !canEdit || ['qc_check', 'review', 'outcome'].includes(stage),
        callback: (document: any, event: string, _: string) => {
          return event === 'process_action'
            ? uploadRequiredDoc({
                document,
                isUploaded: item.isUploaded,
              })
            : onCancelUpload(document.id);
        },
        columnKey: cellData,
      },
    },
  };
}

export function getChatCell(
  cellData: string,
  item: any,
  isInternal: boolean,
  canEdit: boolean,
  onOpenChat: (document: Document) => void,
): any {
  const isChatComponent = item.isUploaded && (isInternal ? canEdit : true);
  return {
    [cellData]: {
      componentType: isChatComponent ? DocumentChatCellComponent : 'default',
      cellData: '',
      componentConfig: isChatComponent
        ? {
            rowData: item,
            callback: (doc: any) => {
              if (!isInternal && !doc.messages) return;
              onOpenChat(doc);
            },
          }
        : {},
    },
  };
}

export function getQCStatusCell(cellData: string, item: any): any {
  return {
    [cellData]: {
      componentType: DocumentQcStatusCellComponent,
      cellData: item.qcStatus,
      componentConfig: {
        rowData: item,
        checkColumn: 'is_complete',
      },
    },
  };
}

export function getActionCell(
  cellData: string,
  item: any,
  index: number,
  canDelete: boolean,
  setSubmissionDocument: (doc: any) => void,
  openDoc: () => void,
  downloadDoc: () => void,
  deleteDoc: () => void,
): any {
  const menuItems = [
    {
      title: 'Open',
      callback: (doc: any) => {
        setSubmissionDocument(doc);
        openDoc();
      },
    },
    {
      title: 'Download',
      callback: (doc: any) => {
        setSubmissionDocument(doc);
        downloadDoc();
      },
    },
    ...(canDelete
      ? [
          {
            title: 'Delete',
            callback: (doc: any) => {
              setSubmissionDocument(doc);
              deleteDoc();
            },
          },
        ]
      : []),
  ];

  return {
    [cellData]: {
      componentType: item.isUploaded ? 'kebabmenu' : 'default',
      cellData: 'N/A',
      componentConfig: {
        icon: 'more_vert',
        eventParams: item,
        menuItems,
        rowIndex: index,
        columnKey: 'actions',
      },
    },
  };
}

export function getDocumentTypeCell(
  cellData: string,
  item: any,
  index: number,
  documentTypes: DocumentType[],
  canEditDocType: boolean,
): any {
  const options = documentTypes.map((doc) => ({
    ...doc,
    label: doc.name,
  }));
  const isDropdown = item.isUploaded && canEditDocType;

  return {
    [cellData]: {
      componentType: isDropdown ? 'dropdown' : 'default',
      cellData: isDropdown ? item.document_type_id : item.document_type_name,
      componentConfig: {
        options,
        selectedOption: options.find((doc) => doc.id === item.document_type_id),
        rowIndex: index,
        columnKey: cellData,
      },
    },
  };
}

export function getDocumentNameCell(
  cellData: string,
  item: any,
  index: number,
  exportable: boolean,
  requiredDocumentTypes: RequiredDocumentType[],
  canEditReqDocType: boolean,
  onSelectDocument: () => void,
): any {
  if (exportable) {
    return {
      [cellData]: {
        componentType: 'default',
        cellData: item.name ?? 'N/A',
      },
    };
  }
  const options = [
    { id: `optional-${item.document_type_id}`, label: 'Optional' },
    ...requiredDocumentTypes.map((doc) => ({
      ...doc,
      label: doc?.document_type?.title ?? '',
    })),
  ];
  const isDropdown = item.isUploaded && canEditReqDocType;
  const componentType = isDropdown ? 'dropdown' : 'default';
  let component;
  if (isDropdown) {
    const selectedOption = options.find(
      (doc) => doc.label === (item.name || item.document_name),
    );
    component = {
      cellData: item.name || 'N/A',
      componentType,
      componentConfig: {
        options,
        selectedOption,
        rowData: item,
        rowIndex: index,
        callback: () => {
          onSelectDocument();
        },
        columnKey: cellData,
      },
    };
  } else {
    component = {
      componentType,
      cellData: item.name || item.document_name || 'N/A',
    };
  }
  return {
    [cellData]: component,
  };
}

export function getWCGNameCell(cellData: string, item: any): any {
  // TODO: rework cellData with title
  const component = {
    componentType: 'default',
    componentConfig: {
      columnKey: cellData,
      rowData: item,
    },
    cellData: item.isUploaded
      ? item.wcg_name.lastIndexOf('.') !== -1
        ? item.wcg_name.substring(0, item.wcg_name.lastIndexOf('.'))
        : item.wcg_name
      : '',
  };
  return {
    [cellData]: component,
  };
}

export function formatDate(
  date?: string,
  wcgDateTime?: WcgDateTimePipe,
): string {
  return date && date !== 'None' && wcgDateTime
    ? wcgDateTime.transform(date)
    : '';
}

export function buildDocs(
  documents: Document[],
  requiredDocumentTypes: RequiredDocumentType[],
  wcgDateTime?: WcgDateTimePipe,
): DocumentListItem[] {
  return documents.map((doc: any) => {
    const matchedType = requiredDocumentTypes.find(
      (docType) => docType.document_type.title === doc.name,
    );
    const isNameOptional =
      (doc?.name ?? doc?.data?.document_name ?? '').toLowerCase() ===
      'optional';

    const isRequired = matchedType && !matchedType.optional && !isNameOptional;

    const attributeName = doc.attribute_type?.name || '';
    const documentTypeName = doc?.document_type?.name;

    return {
      attribute: attributeName,
      attribute_type_name: attributeName,
      created_dt: doc.data?.created_dt ?? doc.created_dt,
      complete_timestamp: doc.complete_timestamp
        ? formatDate(doc.complete_timestamp, wcgDateTime)
        : '',
      dataItem: doc.data,
      docRequired: isRequired ? 'Yes' : 'No',
      document_name: doc?.name || doc.data?.document_name || 'Optional',
      document_type_id: doc?.document_type?.id,
      document_type: documentTypeName ?? '',
      document_type_name: documentTypeName,
      entity_id: doc.entity_id,
      entity_type: doc.entity_type,
      file_name: doc.title ?? 'Upload file',
      id: doc.id,
      isResolved: doc.is_resolved,
      isUploaded: true,
      is_additional: !isRequired,
      is_complete: !!doc.is_complete,
      messages: doc.messages ? doc.messages : 0,
      name: doc?.name ?? doc?.title?.substring(0, doc?.title?.lastIndexOf('.')),
      qcStatus: doc.is_complete ? 'Checked' : 'Not checked',
      updated_dt: doc.is_complete
        ? formatDate(doc.complete_timestamp, wcgDateTime)
        : '',
      title: doc.title ?? 'Upload file',
      wcg_name: doc.wcg_name ?? doc.title,
    };
  });
}

export function buildRequiredDocTypes(
  requiredDocs: RequiredDocumentType[],
): DocumentListItem[] {
  return requiredDocs.map((docType: any) => ({
    id: docType.id,
    name: `${docType.document_type.title ?? docType.document_type.name}`,
    qcStatus: 'Not checked',
    title: `Upload file`,
    docRequired: 'Yes',
    isUploaded: false,
    is_additional: false,
    attribute: '',
    attribute_type_name: '',
    created_dt: '',
    updated_dt: '',
    is_complete: false,
    document_type_id: docType.document_type.id,
    document_type: docType.document_type.name,
    document_type_name: docType.document_type.name,
    document_name: docType.document_type.title,
    isFakeDocument: true,
    questionId: docType.document_type.questionId,
    answerId: docType.document_type.answerId,
    wcg_name: '',
  }));
}

export function buildUploadingDocs(
  documents: InProgressDocument[],
  requiredDocumentTypes: RequiredDocumentType[],
  wcgDateTime?: WcgDateTimePipe,
): DocumentListItem[] {
  return documents.map((doc) => {
    if (doc.document) {
      const listItem = buildDocs(
        [doc.document],
        requiredDocumentTypes,
        wcgDateTime,
      )[0];
      return {
        ...listItem,
        complete_timestamp: doc.complete_timestamp,
        documentId: doc.document.id,
        document_name: doc.documentName,
        document_type_id: doc.documentTypeId,
        document_type_name: doc.documentTypeName,
        isUploadedDocument: true,
        newUpload: true,
        uploadProgress: 100,
        uploadSuccess: true,
      };
    }

    const isRequired = !!requiredDocumentTypes.find((docType) => {
      return docType.document_type.title === doc.name;
    });

    return {
      attribute: '',
      attribute_type_name: '',
      created_dt: '',
      docRequired: isRequired ? 'Required' : 'Optional',
      document_name: doc.documentName,
      document_type_id: doc.documentTypeId,
      document_type: doc.documentTypeName ?? '',
      document_type_name: doc.documentTypeName,
      error: doc.error || '',
      id: doc.id,
      isError: !!doc.error,
      isUploaded: false,
      isUploading: true,
      is_additional: !isRequired,
      is_complete: false,
      name: doc.name,
      newUpload: true,
      qcStatus: 'Uploading',
      questionId: isRequired ? doc.questionId : null,
      title: '',
      updated_dt: '',
      uploadProgress: doc.progress || 0,
    };
  });
}

export function getDefaultAdvancedFilters(
  _documentTypes: DocumentType[],
  _documentNames: RequiredDocumentType[],
  _attributeTypes: DocumentAttributeType[],
  columns: HeaderCellConfiguration[],
) {
  const filterConfig: Record<string, any> = {
    title: {
      type: WcgAdvancedFilterType.StringSearch,
      label: 'File Name',
      columnKey: 'title',
    },
    document_type_name: {
      type: WcgAdvancedFilterType.MultiSelect,
      label: 'Document Type',
      columnKey: 'document_type',
      options: mapFilteredDocumentTypes(_documentTypes),
    },
    document_name: {
      type: WcgAdvancedFilterType.MultiSelect,
      label: 'Document Name',
      columnKey: 'document_name',
      options: [
        { label: 'Optional', value: 'Optional' },
        ...(_documentNames?.map((docType) => ({
          label: docType.document_type.title,
          value: docType.document_type.title,
        })) ?? []),
      ],
    },
    docRequired: {
      type: WcgAdvancedFilterType.StringSearch,
      label: 'Required',
      columnKey: 'docRequired',
    },
    created_dt: {
      type: WcgAdvancedFilterType.DateRange,
      label: 'Upload Date',
      columnKey: 'created_dt',
    },
    name: {
      type: WcgAdvancedFilterType.StringSearch,
      label: 'WCG Name',
      columnKey: 'name',
    },
    attribute: {
      type: WcgAdvancedFilterType.MultiSelect,
      label: 'Attribute',
      columnKey: 'attribute_type_name',
      options: mapFilteredAttributeTypes(_attributeTypes),
    },
    updated_dt: {
      type: WcgAdvancedFilterType.DateRange,
      label: 'QC Date',
      columnKey: 'updated_dt',
    },
    // Exclude these columns explicitly
    action: null,
    messages: null,
    qcStatus: null,
  };

  // Build the advanced filters object based on column definitions
  const advancedFilters = columns.reduce((filters: any, column: any) => {
    const config = filterConfig[column.columnKey];
    if (config) {
      filters[column.columnKey] = config;
    }
    return filters;
  }, {});

  return advancedFilters;
}

export function findRequiredDocumentType(
  typeId: string,
  requiredDocumentTypes: RequiredDocumentType[],
) {
  return requiredDocumentTypes.find(
    (type) => type?.document_type?.id === typeId,
  );
}

export function mapFilteredDocumentTypes(
  documentTypes: DocumentTypeFilter[],
): { label: string; value: string }[] {
  return (
    documentTypes?.map((type: any) => ({
      label: type.name,
      value: type.name,
    })) ?? []
  );
}

export function mapFilteredAttributeTypes(
  attributeTypes: DocumentAttributeType[],
): { label: string; value: string }[] {
  return (
    attributeTypes?.map((type) => ({
      label: type.name,
      value: type.name,
    })) ?? []
  );
}
